import React, { useEffect, useState, useCallback } from "react";
import { X, Play, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { fetchMedia } from "../lib/api";

/**
 * Reusable premium media grid with lightbox for photos + inline video playback.
 *
 * Props:
 * - category: string — Media category slug to filter by
 * - columns: 2 | 3 | 4 — default 3
 * - emptyState: JSX — shown when no media for this category (optional)
 * - variant: "grid" | "masonry" — layout style
 */
const MediaGallery = ({ category, subcategory, columns = 3, emptyState = null, variant = "grid" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(-1);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { category };
    if (subcategory) params.subcategory = subcategory;
    fetchMedia(params)
      .then((data) => alive && setItems(data || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [category, subcategory]);

  const close = useCallback(() => setOpenIdx(-1), []);
  const prev = useCallback(() => setOpenIdx((i) => (i > 0 ? i - 1 : items.length - 1)), [items.length]);
  const next = useCallback(() => setOpenIdx((i) => (i < items.length - 1 ? i + 1 : 0)), [items.length]);

  useEffect(() => {
    if (openIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIdx, close, prev, next]);

  if (loading) {
    return (
      <div data-testid={`gallery-${category}-loading`} className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Array.from({ length: columns * 2 }).map((_, i) => (
          <div key={i} className="aspect-[4/5] bg-white/[0.03] animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    if (emptyState) return emptyState;
    return (
      <div
        data-testid={`gallery-${category}-empty`}
        className="border border-dashed border-white/10 py-20 text-center"
      >
        <ImageOff size={28} className="text-white/20 mx-auto mb-4" strokeWidth={1.2} />
        <p className="label-caps text-white/40 text-[11px]">Cette section sera bientôt enrichie</p>
      </div>
    );
  }

  const colClass = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[columns];
  const spanFor = (i) => {
    if (variant !== "masonry") return "";
    // Deterministic layout — every 5th tile wider, every 7th taller
    if (i % 5 === 0) return "lg:col-span-2";
    return "";
  };
  const openItem = items[openIdx];

  return (
    <>
      <div data-testid={`gallery-${category}`} className={`grid grid-cols-2 ${colClass} gap-3 md:gap-4`}>
        {items.map((m, idx) => (
          <button
            key={m.id}
            data-testid={`gallery-${category}-item-${idx}`}
            onClick={() => setOpenIdx(idx)}
            className={`group relative overflow-hidden bg-black aspect-[4/5] cursor-zoom-in ${spanFor(idx)}`}
          >
            {m.media_type === "video" ? (
              <>
                <video
                  src={m.media_url}
                  poster={m.thumbnail_url || undefined}
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                  <div className="w-14 h-14 rounded-full border border-white/60 flex items-center justify-center backdrop-blur-sm bg-black/30">
                    <Play size={20} className="text-white ml-0.5" strokeWidth={1.4} />
                  </div>
                </div>
              </>
            ) : (
              <img
                src={m.media_url}
                alt={m.title || "MAISON DOUBA"}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
            {m.title && (
              <div className="absolute bottom-0 left-0 right-0 p-4 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="label-caps text-[color:var(--gold)] text-[9px] mb-1">MAISON DOUBA</p>
                <p className="text-white font-serif text-sm leading-tight line-clamp-2">{m.title}</p>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {openItem && (
        <div
          data-testid={`gallery-${category}-lightbox`}
          className="fixed inset-0 z-[999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
          onClick={close}
        >
          <button
            onClick={close}
            data-testid="lightbox-close"
            className="absolute top-6 right-6 text-white/70 hover:text-[color:var(--gold)] p-2"
            aria-label="Fermer"
          >
            <X size={22} strokeWidth={1.2} />
          </button>
          {items.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 md:left-8 text-white/60 hover:text-[color:var(--gold)] p-3"
                aria-label="Précédent"
              >
                <ChevronLeft size={30} strokeWidth={1.2} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 md:right-8 text-white/60 hover:text-[color:var(--gold)] p-3"
                aria-label="Suivant"
              >
                <ChevronRight size={30} strokeWidth={1.2} />
              </button>
            </>
          )}
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-6xl w-full max-h-[86vh] flex flex-col items-center gap-6"
          >
            {openItem.media_type === "video" ? (
              <video
                src={openItem.media_url}
                controls
                autoPlay
                playsInline
                className="max-h-[74vh] w-auto max-w-full"
              />
            ) : (
              <img
                src={openItem.media_url}
                alt={openItem.title || ""}
                className="max-h-[74vh] w-auto max-w-full object-contain"
              />
            )}
            {(openItem.title || openItem.description) && (
              <div className="text-center max-w-2xl">
                {openItem.title && (
                  <p className="font-serif text-white text-lg md:text-xl italic">{openItem.title}</p>
                )}
                {openItem.description && (
                  <p className="text-white/50 text-sm mt-2 font-light">{openItem.description}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MediaGallery;
